
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { assignUserErpWarehouseRecord } from "redux/authorization/userSlice";
import { useDispatch } from "react-redux";
import React, { useEffect, useState, useCallback } from "react";
import Select from 'react-select';
import PropTypes from 'prop-types';
import { FaInfoCircle, FaBoxOpen } from "react-icons/fa";
import { selectIconComponents, reactSelectCompactStyles } from 'helpers/reactSelectIconControl';
import { getWarehouses } from "redux/erp/itemSlice";

// Stable reference — see note in AssignRole; inline components break the menu.
const WAREHOUSE_SELECT_COMPONENTS = selectIconComponents(<FaBoxOpen size={14} className="tw-text-amber-500" />);

const RecordLevelErpWarehouse = ({ userId, data = [] }) => {
  const { handleSubmit, setValue } = useForm();
  const [selectedWarehouses, setSelectedWarehouses] = useState([]);
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();

  // Fetch warehouse options on component mount
  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        setIsLoading(true);
        const response = await dispatch(getWarehouses()).unwrap();
        const warehouseData = response.rows;
        const options = warehouseData
          .map(item => item.attributes)
          .map(item => ({ value: item.id, label: item.name }));
        setWarehouseOptions(options);
      } catch (error) {
        console.error('Error fetching warehouses:', error);
        toast.error('Failed to load warehouses');
      } finally {
        setIsLoading(false);
      }
    };

    fetchWarehouses();
  }, [dispatch]);

  // Set selected warehouses from data prop
  useEffect(() => {
    if (data && data.length > 0) {
      const selected = data.map(item => ({ 
        value: item.id, 
        label: item.name 
      }));
      setSelectedWarehouses(selected);
      
      // Set form value
      const warehouseIds = data.map(item => item.id);
      setValue('warehouse_ids', warehouseIds);
    } else {
      setSelectedWarehouses([]);
      setValue('warehouse_ids', []);
    }
  }, [data, setValue]);

  const handleChangeWarehouse = useCallback((selectedOptions) => {
    const options = selectedOptions || [];
    const selectedIds = options.map(option => option.value);
    
    setValue('warehouse_ids', selectedIds, { shouldValidate: true });
    setSelectedWarehouses(options);
  }, [setValue]);

  const onSubmit = async (formData) => {
    try {
      if (!userId) {
        toast.error('User ID is required');
        return;
      }

      await dispatch(assignUserErpWarehouseRecord({ 
        userId, 
        data: formData 
      })).unwrap();
      
      toast.success('Warehouses updated successfully');
    } catch (error) {
      console.error('Error updating warehouses:', error);
      toast.error(error?.message || 'Failed to update warehouses');
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="tw-mb-2 tw-text-xs">
          <Select
            components={WAREHOUSE_SELECT_COMPONENTS}
            styles={reactSelectCompactStyles}
            closeMenuOnSelect={false}
            options={warehouseOptions}
            value={selectedWarehouses}
            placeholder="Select warehouses..."
            classNamePrefix="react-select"
            isMulti
            onChange={handleChangeWarehouse}
            isLoading={isLoading}
            isDisabled={isLoading}
            noOptionsMessage={() => "No warehouses available"}
          />
        </div>
        
        <div className="tw-flex tw-justify-end tw-mt-2">
          <button type="submit" disabled={isLoading} className="tw-inline-flex tw-items-center tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-rounded-md tw-border tw-border-gray-300 tw-bg-white hover:tw-bg-gray-50 tw-text-gray-700 tw-cursor-pointer tw-transition-colors disabled:tw-opacity-50">
            {data?.length > 0 ? "Update" : "Add"} Warehouse
          </button>
        </div>
      </form>
      
      <div className="tw-mt-4 tw-p-3 tw-bg-slate-50 tw-rounded-md tw-text-xs">
        <FaInfoCircle className="tw-text-cyan-600 tw-mr-2" />
        Assigning warehouses will limit record access for inventory transactions.
      </div>
    </>
  );
};

RecordLevelErpWarehouse.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  data: PropTypes.array
};

export default RecordLevelErpWarehouse;